import { z } from 'zod';
import { CustomFieldValueSchema } from './custom-field-value.dto';
import { ArrayFilterSchema, DateFilterSchema, FieldFilterSchema } from './idea-view.dto';
import { inferApiResultSchema, inferPaginatedApiResponse } from './infer-api';

export const FeedbackFilter = {
  AllFeedback: 'all',
  UnprocessedFeedback: 'unprocessed',
  ProcessedFeedback: 'processed',
} as const;

export const FeedbackFormQueryFilterSchema = z.object({
  productAreas: ArrayFilterSchema.nullish(),
});

export type FeedbackFormQueryFilterSchemaDto = z.infer<typeof FeedbackFormQueryFilterSchema>;

export const FeedbackFilterSchema = z.object({
  ids: ArrayFilterSchema.nullish(),
  fields: z.array(FieldFilterSchema).nullish(),
  status: ArrayFilterSchema.nullish(),
  productAreas: ArrayFilterSchema.nullish(),
  customers: ArrayFilterSchema.nullish(),
  contacts: ArrayFilterSchema.nullish(),
  forms: ArrayFilterSchema.nullish(),
  submitted: DateFilterSchema.nullish(),
});
export type FeedbackFilterSchemaDto = z.infer<typeof FeedbackFilterSchema>;

export type FeedbackFilterType = (typeof FeedbackFilter)[keyof typeof FeedbackFilter];

export const FeedbackViewSchema = z.nativeEnum(FeedbackFilter);

export const FeedbackStatusEnum = z.enum(['RECEIVED', 'UNDER_CONSIDERATION', 'CLOSED']);
export type FeedbackStatus = z.infer<typeof FeedbackStatusEnum>;

export const FeedbackSchema = z.object({
  id: z.number(),
  accountId: z.string().max(50),
  feedbackFormId: z.number().nullable(),
  summary: z.string(),
  status: FeedbackStatusEnum,
  fields: z.array(CustomFieldValueSchema),
  createdAt: z.date(),
  processedAt: z.date().nullable(),
  deletedAt: z.coerce.date().nullish(),
  archivedAt: z.coerce.date().nullish(),
  customerId: z.number().nullable(),
  contactId: z.number().nullable(),
});

export const FeedbackSummaryItemSchema = FeedbackSchema.extend({
  attachmentCount: z.number().default(0),
  commentCount: z.number().default(0),
  productAreas: z.array(
    z.object({ name: z.string(), id: z.number(), archivedAt: z.coerce.date().nullish() }),
  ),
  email: z.string().email(),
  firstName: z.string().trim(),
  lastName: z.string().trim(),
  feedbackFormName: z.string().trim().nullable(),
});

export const FeedbackSummarySchema = z.array(FeedbackSummaryItemSchema);

export const FeedbackStatusSummarySchema = z.object({
  received: z.number(),
  underConsideration: z.number(),
  closed: z.number(),
  total: z.number(),
});
export type FeedbackStatusSummaryDto = z.infer<typeof FeedbackStatusSummarySchema>;

export const FeedbackDescriptionFieldId = -1;
export const FeedbackAttachmentsFieldId = -2;

export type FeedbackDto = z.infer<typeof FeedbackSchema>;
export type FeedbackSummaryItemDto = z.infer<typeof FeedbackSummaryItemSchema>;
export type FeedbackSummaryDto = z.infer<typeof FeedbackSummarySchema>;
export const GetFeedbackResponseSchema = inferApiResultSchema(FeedbackSchema);
export type GetFeedbackResponseDto = z.infer<typeof GetFeedbackResponseSchema>;

export const GetFeedbackSummaryResponseSchema = inferApiResultSchema(FeedbackSummaryItemSchema);
export type GetFeedbackSummaryResponseDto = z.infer<typeof GetFeedbackSummaryResponseSchema>;

export const GetFeedbacksResponseSchema = inferPaginatedApiResponse(FeedbackSummarySchema);
export type GetFeedbacksResponseDto = z.infer<typeof GetFeedbacksResponseSchema>;
export const CreateFeedbackSchema = FeedbackSchema.omit({
  id: true,
  accountId: true,
  createdAt: true,
  processedAt: true,
  deletedAt: true,
  archivedAt: true,
}).extend({
  captchaToken: z.string().nullable(),
  recaptchaSiteKey: z.string().nullable(),
  customerId: z.number().nullable().default(null),
  contactId: z.number().nullable().default(null),
});

export const UpdateFeedbackSchema = FeedbackSchema.omit({
  id: true,
  accountId: true,
  createdAt: true,
  deletedAt: true,
  archivedAt: true,
});

export const UpdateFeedbackSchemaPartial = UpdateFeedbackSchema.partial();

export type CreateFeedbackDto = z.infer<typeof CreateFeedbackSchema>;
export const CreateFeedbackResponseSchema = inferApiResultSchema(FeedbackSchema);
export type CreateFeedbackResponseDto = z.infer<typeof CreateFeedbackResponseSchema>;
export type UpdateFeedbackDto = z.infer<typeof UpdateFeedbackSchemaPartial>;
export const UpdateFeedbackResponseSchema = inferApiResultSchema(FeedbackSchema);
export type UpdateFeedbackResponseDto = z.infer<typeof UpdateFeedbackResponseSchema>;

export const CreateExternalFeedbackSchema = CreateFeedbackSchema.omit({
  feedbackFormId: true,
  customerId: true,
  contactId: true,
}).extend({
  formSlug: z.string(),
  portalSlug: z.string(),
});
export type CreateExternalFeedbackDto = z.infer<typeof CreateExternalFeedbackSchema>;

export const CreateInternalFeedbackSchema = z.object({
  summary: z.string().trim().min(1),
  description: z.string().trim().min(1).nullish(),
  status: FeedbackStatusEnum.default('RECEIVED'),
  productAreaIds: z.array(z.number().int().positive()).min(1),
  answers: z
    .array(
      z.object({
        fieldId: z.number().int(),
        values: z.array(z.string()),
      }),
    )
    .default([]),
});
export type CreateInternalFeedbackDto = z.infer<typeof CreateInternalFeedbackSchema>;

export const FeedbackAttachmentSchema = z.object({
  id: z.number(),
  feedbackId: z.number(),
  name: z.string(),
  size: z.number(),
  attachmentKey: z.string(),
  attachmentUrl: z.string().max(2083),
  mimeType: z.string(),
  createdAt: z.date(),
  userId: z.string().max(50).nullable(),
  contactId: z.number().nullable(),
});
export type FeedbackAttachmentDto = z.infer<typeof FeedbackAttachmentSchema>;
export const GetFeedbackAttachmentResponseSchema = inferApiResultSchema(FeedbackAttachmentSchema);
export type GetFeedbackAttachmentResponseDto = z.infer<typeof GetFeedbackAttachmentResponseSchema>;
export const GetFeedbackAttachmentsResponseSchema = inferApiResultSchema(
  z.array(FeedbackAttachmentSchema),
);
export type GetFeedbackAttachmentsResponseDto = z.infer<
  typeof GetFeedbackAttachmentsResponseSchema
>;
export const CreateFeedbackAttachmentSchema = FeedbackAttachmentSchema.omit({
  id: true,
  attachmentUrl: true,
  createdAt: true,
});
export type CreateFeedbackAttachmentDto = z.infer<typeof CreateFeedbackAttachmentSchema>;
export const CreateFeedbackAttachmentResponseSchema = inferApiResultSchema(
  FeedbackAttachmentSchema.omit({
    attachmentUrl: true,
  }),
);
export type CreateFeedbackAttachmentResponseDto = z.infer<
  typeof CreateFeedbackAttachmentResponseSchema
>;
export const UpdateFeedbackAttachmentSchema = CreateFeedbackAttachmentSchema.partial();
export type UpdateFeedbackAttachmentDto = z.infer<typeof UpdateFeedbackAttachmentSchema>;
export const UpdateFeedbackAttachmentResponseSchema =
  inferApiResultSchema(FeedbackAttachmentSchema);
export type UpdateFeedbackAttachmentResponseDto = z.infer<
  typeof UpdateFeedbackAttachmentResponseSchema
>;

export const FeedbackCommentSchema = z.object({
  id: z.number(),
  feedbackId: z.number(),
  commenterId: z.string(),
  content: z.string(),
  createdAt: z.date(),
  customerVisible: z.boolean(),
  customerId: z.number().nullish(),
  contactId: z.number().nullish(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  deletedAt: z.date().nullish(),
  archivedAt: z.date().nullish(),
});

export type FeedbackCommentDto = z.infer<typeof FeedbackCommentSchema>;
export const GetFeedbackCommentResponseSchema = inferApiResultSchema(FeedbackCommentSchema);
export type GetFeedbackCommentResponseDto = z.infer<typeof GetFeedbackCommentResponseSchema>;
export const GetFeedbackCommentsResponseSchema = inferPaginatedApiResponse(
  z.array(FeedbackCommentSchema),
);
export type GetFeedbackCommentsResponseDto = z.infer<typeof GetFeedbackCommentsResponseSchema>;
export const CreateFeedbackCommentSchema = FeedbackCommentSchema.omit({
  id: true,
  createdAt: true,
  firstName: true,
  lastName: true,
  email: true,
  deletedAt: true,
  archivedAt: true,
}).extend({
  customerId: z.number().nullish().default(null),
  contactId: z.number().nullish().default(null),
});
export type CreateFeedbackCommentDto = z.infer<typeof CreateFeedbackCommentSchema>;
export const CreateFeedbackCommentResponseSchema = inferApiResultSchema(FeedbackCommentSchema);
export type CreateFeedbackCommentResponseDto = z.infer<typeof CreateFeedbackCommentResponseSchema>;
export const UpdateFeedbackCommentSchema = CreateFeedbackCommentSchema.partial();
export type UpdateFeedbackCommentDto = z.infer<typeof UpdateFeedbackCommentSchema>;
export const UpdateFeedbackCommentResponseSchema = inferApiResultSchema(FeedbackCommentSchema);
export type UpdateFeedbackCommentResponseDto = z.infer<typeof UpdateFeedbackCommentResponseSchema>;
